/**
 * TV Navigation Engine
 * Core engine that handles navigation logic, node management, and focus control
 */

import { RefObject } from 'react';
import type { 
  NavigationNode, 
  PageNavigationMap, 
  NavigationDirection,
  findNodeInMap 
} from './tvNavigationMap';
import { findNodeInMap as findNodeHelper } from './tvNavigationMap';
import { debugLog, debugWarn, debugError } from '../lib/debug';

/**
 * TV Navigation Engine class
 * Manages navigation state and handles focus movement
 */
export class TVNavigationEngine {
  private currentMap: PageNavigationMap | null = null;
  private currentNodeId: string | null = null;
  private nodeRefs: Map<string, RefObject<HTMLElement>> = new Map();
  private nodeRegistry: Map<string, NavigationNode> = new Map();
  private focusHistory: string[] = [];
  private maxHistoryLength = 50;
  private rectCache = new Map<string, { rect: DOMRect; timestamp: number }>();
  private readonly CACHE_TTL = 100; // 100ms cache

  /**
   * Load navigation map for a page
   */
  loadMap(pageId: string, map: PageNavigationMap): void {
    this.currentMap = map;
    this.currentNodeId = null;
    this.nodeRegistry.clear();
    
    // Register all nodes from the map
    for (const zone of Object.values(map.zones)) {
      for (const node of zone.nodes) {
        this.nodeRegistry.set(node.id, { ...node });
      }
    }
    
    // Set default focus if specified
    const defaultZone = Object.values(map.zones).find(zone => zone.defaultFocus);
    if (defaultZone?.defaultFocus) {
      this.currentNodeId = defaultZone.defaultFocus;
    }
  }

  /**
   * Register a node's ref at runtime
   */
  registerNode(nodeId: string, ref: RefObject<HTMLElement>): void {
    this.nodeRefs.set(nodeId, ref);
    
    // Update node in registry if it exists
    const node = this.nodeRegistry.get(nodeId);
    if (node) {
      node.ref = ref;
    } else {
      // If node doesn't exist in map, create a dynamic node
      this.nodeRegistry.set(nodeId, {
        id: nodeId,
        type: 'card',
        component: 'DynamicNode',
        position: { zone: 'content' },
        neighbors: {},
        ref,
        metadata: { isDynamic: true }
      });
    }
  }

  /**
   * Register a complete node (useful for dynamic nodes)
   */
  registerNodeWithData(node: NavigationNode): void {
    this.nodeRegistry.set(node.id, node);
    if (node.ref) {
      this.nodeRefs.set(node.id, node.ref);
    }
  }

  /**
   * Unregister a node
   */
  unregisterNode(nodeId: string): void {
    this.nodeRefs.delete(nodeId);
    // Don't delete from registry immediately, might be needed for navigation
    // Only delete if it's a dynamic node
    const node = this.nodeRegistry.get(nodeId);
    if (node?.metadata?.isDynamic) {
      this.nodeRegistry.delete(nodeId);
    }
    
    // If this was the current node, clear current focus
    if (this.currentNodeId === nodeId) {
      this.currentNodeId = null;
    }
  }

  /**
   * Update node neighbors (useful for dynamic rows)
   */
  updateNodeNeighbors(nodeId: string, neighbors: Partial<NavigationNode['neighbors']>): void {
    const node = this.nodeRegistry.get(nodeId);
    if (node) {
      node.neighbors = { ...node.neighbors, ...neighbors };
      debugLog(`[TVNavigation] Updated neighbors for ${nodeId}:`, node.neighbors);
    } else {
      debugWarn(`[TVNavigation] Cannot update neighbors: Node ${nodeId} not found in registry`);
      // If node doesn't exist in registry, try to find it and create it
      if (this.currentMap) {
        const mapNode = findNodeHelper(this.currentMap, nodeId);
        if (mapNode) {
          // Create node in registry from map node
          this.nodeRegistry.set(nodeId, {
            ...mapNode,
            neighbors: { ...mapNode.neighbors, ...neighbors }
          });
          debugLog(`[TVNavigation] Created node ${nodeId} in registry with updated neighbors`);
        }
      }
    }
  }

  /**
   * Find a node by ID
   */
  findNode(nodeId: string): NavigationNode | null {
    // First check registry (for dynamically registered nodes)
    const registryNode = this.nodeRegistry.get(nodeId);
    if (registryNode) {
      // If node exists in map, merge map data with registry data (registry takes precedence for neighbors)
      if (this.currentMap) {
        const mapNode = findNodeHelper(this.currentMap, nodeId);
        if (mapNode) {
          // Merge: registry data takes precedence, especially for neighbors (updated at runtime)
          return { 
            ...mapNode, 
            ...registryNode, 
            neighbors: { ...mapNode.neighbors, ...registryNode.neighbors } // Registry neighbors override map neighbors
          };
        }
      }
      // Node only in registry (dynamically registered)
      return registryNode;
    }
    
    // Fallback to map if not in registry
    if (this.currentMap) {
      const node = findNodeHelper(this.currentMap, nodeId);
      if (node) {
        return node;
      }
    }
    
    return null;
  }

  /**
   * Find all nodes in a row
   */
  findRowNodes(rowId: string): NavigationNode[] {
    const nodes: NavigationNode[] = [];
    for (const [id, node] of this.nodeRegistry.entries()) {
      if (node.metadata?.rowId === rowId && node.type === 'card') {
        nodes.push(node);
      }
    }
    return nodes.sort((a, b) => (a.metadata?.cardIndex || 0) - (b.metadata?.cardIndex || 0));
  }

  /**
   * Find row by index
   */
  findRowByIndex(rowIndex: number): NavigationNode | null {
    for (const node of this.nodeRegistry.values()) {
      if (node.type === 'row' && node.position.rowIndex === rowIndex) {
        return node;
      }
    }
    return null;
  }

  /**
   * Calculate vertical navigation target for cards (spatial positioning based on actual DOM position)
   */
  calculateVerticalTarget(currentNode: NavigationNode, direction: 'up' | 'down'): string | undefined {
    const { rowIndex, columnIndex } = currentNode.position;
    const { rowId } = currentNode.metadata || {};
    
    if (rowIndex === undefined || columnIndex === undefined || !rowId) {
      return undefined;
    }

    // Get the actual DOM element of the current card
    const currentRef = currentNode.ref || this.nodeRefs.get(currentNode.id);
    if (!currentRef?.current) {
      // Fallback to column-based if no ref available
      return this.calculateVerticalTargetByColumn(currentNode, direction);
    }

    // Use cached rect for better performance
    const currentRect = this.getCachedRect(currentRef.current);
    const currentCenterX = currentRect.left + currentRect.width / 2;

    // Find target row
    const targetRowIndex = direction === 'down' ? rowIndex + 1 : rowIndex - 1;
    const targetRow = this.findRowByIndex(targetRowIndex);
    
    if (!targetRow) {
      // No target row found, check if there's a neighbor defined
      return currentNode.neighbors[direction];
    }

    const targetRowId = targetRow.metadata?.rowId;
    if (!targetRowId) {
      return undefined;
    }

    // Find all cards in target row that are actually rendered (have refs)
    const targetRowCardNodes = this.findRowNodes(targetRowId);
    const targetRowCards = targetRowCardNodes
      .map(node => {
        const ref = node.ref || this.nodeRefs.get(node.id);
        if (!ref?.current) return null;
        // Use cached rect for better performance
        const rect = this.getCachedRect(ref.current);
        return { node, ref: ref.current, rect };
      })
      .filter((card): card is { node: NavigationNode; ref: HTMLElement; rect: DOMRect } => card !== null);

    if (targetRowCards.length === 0) {
      // Fallback to column-based if no cards are rendered yet
      return this.calculateVerticalTargetByColumn(currentNode, direction);
    }

    // Find the card whose center X is closest to the current card's center X
    let closestCard = targetRowCards[0];
    let minDistance = Math.abs(targetRowCards[0].rect.left + targetRowCards[0].rect.width / 2 - currentCenterX);

    for (const card of targetRowCards) {
      const cardCenterX = card.rect.left + card.rect.width / 2;
      const distance = Math.abs(cardCenterX - currentCenterX);
      if (distance < minDistance) {
        minDistance = distance;
        closestCard = card;
      }
    }

    return closestCard.node.id;
  }

  /**
   * Fallback: Calculate vertical target by column index (for when DOM refs aren't available)
   */
  private calculateVerticalTargetByColumn(currentNode: NavigationNode, direction: 'up' | 'down'): string | undefined {
    const { rowIndex, columnIndex } = currentNode.position;
    const { rowId } = currentNode.metadata || {};
    
    if (rowIndex === undefined || columnIndex === undefined || !rowId) {
      return undefined;
    }

    const targetRowIndex = direction === 'down' ? rowIndex + 1 : rowIndex - 1;
    const targetRow = this.findRowByIndex(targetRowIndex);
    
    if (!targetRow) {
      return currentNode.neighbors[direction];
    }

    const targetRowId = targetRow.metadata?.rowId;
    if (!targetRowId) {
      return undefined;
    }

    const targetRowCards = this.findRowNodes(targetRowId);
    if (targetRowCards.length === 0) {
      return undefined;
    }

    const targetCardIndex = Math.min(columnIndex, targetRowCards.length - 1);
    const targetNode = targetRowCards[targetCardIndex];
    return targetNode?.id;
  }

  /**
   * Navigate based on direction
   */
  navigate(direction: NavigationDirection): boolean {
    if (!this.currentMap || !this.currentNodeId) {
      debugLog('[TVNavigation] Navigate: No map or currentNodeId');
      return false;
    }

    const currentNode = this.findNode(this.currentNodeId);
    if (!currentNode) {
      debugLog(`[TVNavigation] Navigate: Node not found: ${this.currentNodeId}`);
      return false;
    }

    debugLog(`[TVNavigation] Navigate ${direction} from ${this.currentNodeId}:`, {
      nodeType: currentNode.type,
      neighbors: currentNode.neighbors
    });

    let targetNodeId: string | undefined;

    if (direction === 'up' || direction === 'down') {
      // For vertical navigation, handle special cases
      if (currentNode.type === 'card') {
        // Smart vertical navigation: maintain horizontal position
        targetNodeId = this.calculateVerticalTarget(currentNode, direction);
      } else {
        // Standard vertical navigation
        targetNodeId = currentNode.neighbors[direction];
        debugLog(`[TVNavigation] Vertical navigation ${direction}: ${targetNodeId}`);
      }
    } else {
      // Horizontal navigation
      targetNodeId = currentNode.neighbors[direction];
      debugLog(`[TVNavigation] Horizontal navigation ${direction}: ${targetNodeId}`);
    }

    if (targetNodeId) {
      this.focusNode(targetNodeId);
      return true;
    }

    debugLog(`[TVNavigation] No target node for ${direction} from ${this.currentNodeId}`);
    return false;
  }

  /**
   * Get cached bounding rect for better performance
   */
  private getCachedRect(element: HTMLElement): DOMRect {
    const cacheKey = element.id || `${element.tagName}-${element.className}`;
    const cached = this.rectCache.get(cacheKey);
    const now = Date.now();
    
    if (cached && (now - cached.timestamp) < this.CACHE_TTL) {
      return cached.rect;
    }
    
    const rect = element.getBoundingClientRect();
    this.rectCache.set(cacheKey, { rect, timestamp: now });
    
    // Clean old cache entries if cache gets too large
    if (this.rectCache.size > 50) {
      const entries = Array.from(this.rectCache.entries());
      entries.slice(0, 10).forEach(([key]) => this.rectCache.delete(key));
    }
    
    return rect;
  }

  /**
   * Detect if running on TV device
   */
  private isTVDevice(): boolean {
    return window.matchMedia('(min-width: 1920px)').matches || 
           document.body.classList.contains('tv-device');
  }

  /**
   * Focus a specific node with smart scrolling
   * @param nodeId - The ID of the node to focus
   * @param instantScroll - If true, use instant scroll instead of smooth (for restoration)
   */
  focusNode(nodeId: string, instantScroll: boolean = false): void {
    const node = this.findNode(nodeId);
    if (!node) {
      debugWarn(`[TVNavigation] Node not found: ${nodeId}`);
      return;
    }

    const ref = node.ref || this.nodeRefs.get(nodeId);
    if (!ref?.current) {
      debugWarn(`[TVNavigation] Ref not available for node: ${nodeId}`);
      return;
    }

    // Add to history
    if (this.currentNodeId && this.currentNodeId !== nodeId) {
      this.focusHistory.push(this.currentNodeId);
      if (this.focusHistory.length > this.maxHistoryLength) {
        this.focusHistory.shift();
      }
    }

    // Update current node
    this.currentNodeId = nodeId;

    // Focus the element
    try {
      const element = ref.current;
      element.focus();

      // Detect TV device - use instant scroll for better performance
      const isTV = this.isTVDevice();
      const scrollBehavior = (isTV || instantScroll) ? 'auto' : 'smooth';

      // Determine scroll behavior based on element position and zone
      const isHeaderElement = node.position.zone === 'header';
      const elementRect = this.getCachedRect(element);
      const isTopElement = elementRect.top < 200; // Within 200px of top
      
      // For header elements or elements near top, use 'start' to keep them at top
      // For other elements, use 'center' to ensure full visibility
      const blockOption: ScrollLogicalPosition = (isHeaderElement || isTopElement) 
        ? 'start' 
        : 'center';

      // Handle nested horizontal scroll containers first
      // Traverse up the DOM tree to find horizontal scroll containers
      let parent = element.parentElement;
      const horizontalScrollContainers: HTMLElement[] = [];
      
      while (parent) {
        const style = window.getComputedStyle(parent);
        const hasHorizontalScroll = 
          (style.overflowX === 'auto' || style.overflowX === 'scroll') &&
          parent.scrollWidth > parent.clientWidth;
        
        if (hasHorizontalScroll) {
          horizontalScrollContainers.push(parent);
        }
        parent = parent.parentElement;
      }

      // Scroll horizontal containers first (innermost to outermost) - use instant on TV
      if (horizontalScrollContainers.length > 0) {
        horizontalScrollContainers.reverse().forEach((container) => {
          const containerRect = this.getCachedRect(container);
          const elementCenterX = elementRect.left + elementRect.width / 2;
          const containerCenterX = containerRect.left + containerRect.width / 2;
          
          // Calculate scroll needed to center element in container
          const scrollLeft = container.scrollLeft + (elementCenterX - containerCenterX);
          
          container.scrollTo({
            left: Math.max(0, Math.min(scrollLeft, container.scrollWidth - container.clientWidth)),
            behavior: scrollBehavior // Use instant on TV
          });
        });
      }

      // Then, handle main page scrolling - use instant on TV
      const scrollDelay = (isTV || instantScroll) ? 0 : (horizontalScrollContainers.length > 0 ? 50 : 0);
      
      setTimeout(() => {
        element.scrollIntoView({
          behavior: scrollBehavior, // Instant on TV
          block: blockOption,
          inline: 'center'
        });
      }, scrollDelay);
    } catch (error) {
      debugError(`[TVNavigation] Error focusing node ${nodeId}:`, error);
    }
  }

  /**
   * Get current focused node
   */
  getCurrentNode(): NavigationNode | null {
    if (!this.currentNodeId) {
      return null;
    }
    return this.findNode(this.currentNodeId);
  }

  /**
   * Get current node ID
   */
  getCurrentNodeId(): string | null {
    return this.currentNodeId;
  }

  /**
   * Focus previous node from history
   */
  focusPrevious(): boolean {
    if (this.focusHistory.length > 0) {
      const previousNodeId = this.focusHistory.pop();
      if (previousNodeId) {
        this.focusNode(previousNodeId);
        return true;
      }
    }
    return false;
  }

  /**
   * Clear navigation state
   */
  clear(): void {
    this.currentMap = null;
    this.currentNodeId = null;
    this.focusHistory = [];
    this.nodeRefs.clear();
    this.rectCache.clear(); // Clear rect cache
    // Keep registry for potential restoration
  }

  /**
   * Get all registered nodes (for debugging)
   */
  getAllNodes(): NavigationNode[] {
    return Array.from(this.nodeRegistry.values());
  }

  /**
   * Get navigation map (for debugging)
   */
  getMap(): PageNavigationMap | null {
    return this.currentMap;
  }
}

